import { admittedMulticastEndpoints, CATALOG_NAMESPACE, multicastEndpointSource, withCatalogNamespace } from "@blockcast/mmt-base";
import { admittedEndpointsUnderCatalog, admittedUnderCatalog, isMulticastTrackPacketId, isValidMulticastGroupAddress } from "./endpoint-config.js";

export interface MulticastSignalingEndpoint {
	endpointId: string;
	endpointVersion: number;
	/** The mode `sourceAddress` selects (draft -01 4.1), filled in when the endpoint omits it. */
	protocol: "ssm" | "asm";
	sourceAddress?: string;
	groupAddress: string;
	port: number;
	validUntil?: number;
	tracks: Array<{ name: string; packetId?: number; [key: string]: unknown }>;
	[key: string]: unknown;
}

type Catalog = Record<string, any>;
type Signal = Record<string, any>;

export function createMulticastSignalingReconciler(catalog: Catalog, options: { catalog?: Catalog } = {}) {
	const initial = normalizeCatalogEndpoints(catalog);
	// Signalled endpoints are admitted against the catalog's multicast.auth
	// (draft -01 7.2.6): protected exactly when it is, under its key set. An
	// untagged options.catalog inherits catalog's namespace tag, so the
	// foreign-namespace rule never falls back to track order.
	const tagOf = (value?: Catalog) => (value as Record<PropertyKey, unknown> | undefined)?.[CATALOG_NAMESPACE];
	const tag = tagOf(catalog);
	const authContext = options.catalog && tagOf(options.catalog) === undefined && typeof tag === "string"
		? withCatalogNamespace(options.catalog, tag)
		: options.catalog ?? catalog;
	const endpoints = new Map(initial.map(endpoint => [endpoint.endpointId, copyEndpoint(endpoint)]));
	const versions = new Map(initial.map(endpoint => [endpoint.endpointId, endpoint.endpointVersion]));
	const seen = new Set<string>();
	const dormant = new Set<string>();
	let catalogVersion = Number(catalog?.multicast?.version ?? catalog?.version ?? 0) || 0;
	/**
	 * The endpoints a receiver may use now. It judges the live set -- neither
	 * expired nor invalidated, each already valid on admission -- so the
	 * cross-endpoint rules run over that population, where the catalog path
	 * (`normalizeCatalogEndpoints`) runs them over every catalog endpoint.
	 */
	const snapshot = (now = Date.now()) => {
		const live = [...endpoints.values()].filter(endpoint => !dormant.has(endpoint.endpointId) && !isExpired(endpoint, now));
		// Signals admit one endpoint at a time; the cross-endpoint rules (one
		// endpoint per (S, G, port), no manifestPort clash) need the whole live
		// set, and like a catalog every endpoint they name is ignored.
		const admitted = admittedEndpointsUnderCatalog(authContext, live);
		return {
			catalogVersion,
			endpoints: live.filter((_, index) => admitted.has(index)).map(copyEndpoint),
			dormantEndpointIds: [...dormant].sort(),
		};
	};

	return {
		apply(signal: Signal, now = Date.now()) {
			if (!isObject(signal) || typeof signal.type !== "string" || typeof signal.msgId !== "string") return { applied: false, reason: "invalid-message" };
			if (seen.has(signal.msgId)) return { applied: false, reason: "duplicate-message" };
			if (signal.expiresAt != null && Number(signal.expiresAt) < now) return { applied: false, reason: "expired" };
			const signalCatalogVersion = Number(signal.catalogVersion ?? 0);
			if (!Number.isFinite(signalCatalogVersion)) return { applied: false, reason: "invalid-message" };
			if (signalCatalogVersion < catalogVersion) return { applied: false, reason: "stale-catalog" };
			const result = applyFresh(signal, now);
			if (result.applied) {
				seen.add(signal.msgId);
				// A heartbeat proves liveness but carries no catalog state. Ratcheting
				// here would incorrectly reject the next real snapshot as stale.
				if (signal.type !== "heartbeat") {
					catalogVersion = Math.max(catalogVersion, signalCatalogVersion);
				}
			}
			return result;
		},
		snapshot,
		toCatalog(now = Date.now()) {
			const current = snapshot(now);
			return { ...catalog, multicast: { ...catalog.multicast, version: current.catalogVersion, endpoints: current.endpoints } };
		},
	};

	function applyFresh(signal: Signal, now: number): { applied: boolean; reason?: string; action?: string } {
		switch (signal.type) {
			case "snapshot": {
				const next = signal.payload?.endpoints;
				if (!Array.isArray(next)) return { applied: false, reason: "invalid-message" };
				const normalized = next.map((endpoint, index) => normalizeEndpoint(endpoint, index));
				if (normalized.some(endpoint => !endpoint || !isValidEndpoint(endpoint, options.catalog) || !admittedUnderCatalog(authContext, endpoint))) return { applied: false, reason: "invalid-endpoint" };
				const ids = new Set(normalized.map(endpoint => endpoint!.endpointId));
				if (ids.size !== normalized.length) return { applied: false, reason: "duplicate-endpoint" };
				endpoints.clear(); versions.clear(); dormant.clear();
				for (const endpoint of normalized as MulticastSignalingEndpoint[]) {
					versions.set(endpoint.endpointId, endpoint.endpointVersion);
					if (!isExpired(endpoint, now)) endpoints.set(endpoint.endpointId, copyEndpoint(endpoint));
				}
				return { applied: true, action: "snapshot" };
			}
			case "add": return upsert(signal.payload?.endpoint, now);
			case "update": {
				const id = signal.payload?.endpointId;
				const fields = signal.payload?.fields;
				if (!endpoints.has(id)) return { applied: false, reason: "unknown-endpoint" };
				if (isStale(id, signal.payload?.endpointVersion)) return { applied: false, reason: "stale-endpoint" };
				// `auth` carries a protected ASM endpoint's S, so it is identity too.
				if (!isObject(fields) || ["protocol", "sourceAddress", "groupAddress", "port", "auth"].some(key => Object.hasOwn(fields, key))) return { applied: false, reason: "invalid-update" };
				return upsert({ ...endpoints.get(id), ...fields, endpointVersion: signal.payload.endpointVersion }, now);
			}
			case "remove": {
				const id = signal.payload?.endpointId;
				if (!endpoints.has(id)) return { applied: false, reason: "unknown-endpoint" };
				const endpointVersion = Number(signal.payload?.endpointVersion);
				if (!Number.isFinite(endpointVersion)) return { applied: false, reason: "invalid-message" };
				if (isStale(id, endpointVersion)) return { applied: false, reason: "stale-endpoint" };
				endpoints.delete(id); dormant.delete(id); versions.set(id, endpointVersion);
				return { applied: true, action: "drop" };
			}
			case "invalidate": {
				const id = signal.payload?.endpointId;
				if (!endpoints.has(id)) return { applied: false, reason: "unknown-endpoint" };
				dormant.add(id);
				return { applied: true, action: "drop" };
			}
			case "heartbeat": return { applied: true, action: "heartbeat" };
			default: return { applied: false, reason: "unknown-message-type" };
		}
	}

	function upsert(value: unknown, now: number) {
		const endpoint = normalizeEndpoint(value, endpoints.size);
		if (!endpoint || !isValidEndpoint(endpoint, options.catalog) || !admittedUnderCatalog(authContext, endpoint)) return { applied: false, reason: "invalid-endpoint" };
		if (isStale(endpoint.endpointId, endpoint.endpointVersion)) return { applied: false, reason: "stale-endpoint" };
		versions.set(endpoint.endpointId, endpoint.endpointVersion);
		dormant.delete(endpoint.endpointId);
		if (isExpired(endpoint, now)) endpoints.delete(endpoint.endpointId);
		else endpoints.set(endpoint.endpointId, copyEndpoint(endpoint));
		return { applied: true, action: isExpired(endpoint, now) ? "drop" : "candidate" };
	}

	function isStale(id: string, version: unknown) {
		const current = versions.get(id);
		return current !== undefined && Number(version) <= current;
	}
}

/**
 * The catalog's endpoints a receiver may use. Draft -01 4.1 / 7.2.6 come from
 * `admittedMulticastEndpoints` of `@blockcast/mmt-base`: an endpoint whose
 * mode and `S` disagree, that lacks `auth` under `multicast.auth` (or carries
 * it without), or that clashes on `(S, G, port)` / `manifestPort` is ignored;
 * a catalog with no key set (a repeated `kid` or `firstEpoch`, a malformed or
 * retired root-key `auth`) yields none, never an unauthenticated fallback.
 */
export function normalizeCatalogEndpoints(catalog: Catalog): MulticastSignalingEndpoint[] {
	const endpoints = catalog?.multicast?.endpoints;
	if (!Array.isArray(endpoints)) return [];
	const admitted = admittedMulticastEndpoints(catalog);
	return endpoints
		.map(normalizeEndpoint)
		.filter((endpoint, index): endpoint is MulticastSignalingEndpoint =>
			!!endpoint && admitted.has(index) && isValidEndpoint(endpoint, catalog));
}

function normalizeEndpoint(value: unknown, index: number): MulticastSignalingEndpoint | undefined {
	if (!isObject(value)) return undefined;
	const endpoint = value as Record<string, any>;
	const tracks = Array.isArray(endpoint.tracks) ? endpoint.tracks.map(track => typeof track === "string" ? { name: track } : { ...track }).filter(track => track.name) : [];
	const source = multicastEndpointSource(endpoint);
	return {
		...endpoint,
		...(source ? { protocol: source.mode } : {}),
		// A signalled "5001" is port 5001, so the (S, G, port) rule, which
		// compares with ===, sees it clash with 5001; anything else that is not
		// a number is left for isValidEndpoint to refuse.
		port: typeof endpoint.port === "string" && /^\d+$/.test(endpoint.port) ? Number(endpoint.port) : endpoint.port,
		endpointId: endpoint.endpointId || `${source?.source || ""}|${endpoint.groupAddress || ""}|${endpoint.port || ""}|${index}`,
		endpointVersion: Number(endpoint.endpointVersion ?? 0),
		tracks,
	} as MulticastSignalingEndpoint;
}

function isValidEndpoint(endpoint: MulticastSignalingEndpoint, catalog?: Catalog): boolean {
	if (!endpoint.endpointId || !Number.isFinite(endpoint.endpointVersion)) return false;
	// Mode and S from `sourceAddress` (draft -01 4.1): `protocol` only restates
	// it, and "amt" / "atsc3" are bearers, not protocols.
	if (!multicastEndpointSource(endpoint)) return false;
	if (!isValidMulticastGroupAddress(endpoint.groupAddress) || !Number.isInteger(endpoint.port) || endpoint.port <= 0 || endpoint.port > 65535 || !endpoint.tracks.length) return false;
	const catalogTracks = new Set([...Object.keys(catalog?.video?.renditions ?? {}), ...Object.keys(catalog?.audio?.renditions ?? {})]);
	// Every track must carry a valid packet_id (isMulticastTrackPacketId); one
	// that does not -- including packet_id 0, the MMTP signaling flow --
	// rejects the whole endpoint rather than silently dropping that track.
	return endpoint.tracks.every(track => !!track.name && isMulticastTrackPacketId(track.packetId) && (catalogTracks.size === 0 || catalogTracks.has(track.name) || catalogTracks.has(track.name.replace(/\/repair(?:\/\d+)?$/, ""))));
}

function copyEndpoint(endpoint: MulticastSignalingEndpoint): MulticastSignalingEndpoint {
	return { ...endpoint, tracks: endpoint.tracks.map(track => ({ ...track })) };
}

function isExpired(endpoint: MulticastSignalingEndpoint, now: number): boolean {
	return endpoint.validUntil != null && Number(endpoint.validUntil) < now;
}

function isObject(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object";
}
